import { useState } from "react";
import { api } from "@/lib/api";
import { orgToday } from "@/lib/tz";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarClock, Loader2 } from "lucide-react";

const today = () => orgToday();

// Shown when an address on the participant's file has been edited: re-point that
// participant's rostered shifts at the new address from a chosen date onward.
export function AddressToShifts({ participant, index, original, current, canEdit }) {
  const [date, setDate] = useState(today());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const changed = canEdit && participant && original && current
    && original.trim() && current.trim() && original.trim() !== current.trim();
  if (!changed) return null;

  const body = { old_address: original.trim(), new_address: current.trim(), date_from: date };

  const check = async () => {
    setBusy("check");
    try { setPreview(await api.applyAddressToShifts(participant, { ...body, dry_run: true })); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't check the shifts."); }
    finally { setBusy(""); }
  };

  const apply = async () => {
    setBusy("apply");
    try {
      const r = await api.applyAddressToShifts(participant, body);
      toast.success(r.updated === 0 ? "No shifts needed updating."
        : `${r.updated} shift${r.updated === 1 ? "" : "s"} re-pointed to the new address.`);
      setPreview(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update the shifts."); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 space-y-2" data-testid={`address-to-shifts-${index}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-800">
        <CalendarClock className="h-3.5 w-3.5" /> Address changed — apply to rostered shifts?
      </div>
      <p className="text-[10px] text-amber-800/80">
        Save the participant first, then choose the date the new address takes effect. Shifts on or
        after that date still showing the old address get the new one. Cancelled, approved and
        invoiced shifts are left alone, and past shifts keep the old address.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setPreview(null); }}
          className="h-8 w-40 text-xs" data-testid={`address-to-shifts-date-${index}`} />
        <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={check} disabled={!!busy}
          data-testid={`address-to-shifts-check-${index}`}>
          {busy === "check" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Check shifts"}
        </Button>
        {preview && (
          <Button size="sm" className="h-8 text-[11px] bg-amber-600 hover:bg-amber-700" onClick={apply}
            disabled={!!busy || preview.would_update === 0} data-testid={`address-to-shifts-apply-${index}`}>
            {busy === "apply" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `Update ${preview.would_update} shift${preview.would_update === 1 ? "" : "s"}`}
          </Button>
        )}
      </div>
      {preview && (
        <div className="text-[10px] text-amber-900" data-testid={`address-to-shifts-preview-${index}`}>
          {preview.would_update === 0
            ? "No shifts from that date carry the old address."
            : `${preview.would_update} shift(s) from ${preview.first_date} to ${preview.last_date} will be updated.`}
          {preview.skipped_count > 0 && ` ${preview.skipped_count} skipped (different address, approved or invoiced).`}
        </div>
      )}
    </div>
  );
}
